import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const computeGdsScores = (register, scale) => {
    if (!register || !scale) return null;
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return null;

    const questionById = new Map(questions.map(q => [q.id, q]));

    let totalScore = 0;

    responses.forEach(resp => {
        const q = questionById.get(resp.scaleQuestionId);
        if (!q) return;
        const opt = (q.questionOptionScores || []).find(o => {
            const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
            return Number(sid) === Number(resp.scaleOptionId);
        });
        const score = toNumberOrNull(opt?.score ?? resp.score);
        if (score == null) return;
        totalScore += score;
    });

    let depressionLevel = undefined;
    if (totalScore <= 4) depressionLevel = 'Normal';
    else if (totalScore <= 8) depressionLevel = 'Depresión leve';
    else if (totalScore <= 11) depressionLevel = 'Depresión moderada';
    else depressionLevel = 'Depresión severa';

    return {
        totalScore,
        totalMaxScore: questions.length,
        depressionLevel,
    };
};

export const ScaleResultGDS = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore,
    depressionLevel,
    interpretationItems = [
        { range: '0-4', label: 'Normal' },
        { range: '5-8', label: 'Depresión leve' },
        { range: '9-11', label: 'Depresión moderada' },
        { range: '12-15', label: 'Depresión severa' },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeGdsScores(register, scale), [register, scale]);
    React.useEffect(() => {
            // Usamos onComputedResult, que es lo que recibimos por props
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived); 
            }
        }, [derived, onComputedResult]);

    const fallbackMax = Array.isArray(scale?.questions) ? scale.questions.length : 15;
    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? fallbackMax;
    const displayDepressionLevel = depressionLevel || derived?.depressionLevel;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}/{displayTotalMax}
            </Typography>

            {displayDepressionLevel && (
                <List style={{ paddingTop: 0, marginBottom: 16 }}>
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`Nivel de Depresión: ${displayDepressionLevel}`}
                        />
                    </ListItem>
                </List>
            )}

            <Box mb={1} ml={1}>
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    Interpretación:
                </Typography>
            </Box>

            <List style={{ paddingTop: 0 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.range ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`${item?.range || ''}: ${item?.label || ''}`}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultGDS;
